import { createDefaultExperimentData, DEFAULT_CONCENTRATIONS, DEFAULT_SAMPLE_VOLUMES, DEFAULT_EXPERIMENT_VALUES } from 'constants/experimentDefaults'
import React, { useCallback } from 'react'
import {
  StringParam,
  NumberParam,
  createEnumParam,
  useQueryParams,
  withDefault
} from 'use-query-params'

import { withIgnoreBlanks } from 'utils/custom-query-params'
import { ExperimentData } from '../components/experiment-planning/types'

const defaultExperimentData: ExperimentData = createDefaultExperimentData('imaging')

const useExperimentPlanningParams = (): {
  experimentData: ExperimentData
  setExperimentData: React.Dispatch<React.SetStateAction<ExperimentData>>
} => {
  // Define query parameter configuration
  const [query, setQuery] = useQueryParams({
    experimentGoal: withDefault(
      createEnumParam(['imaging', 'purity', 'yield']), 
      'imaging'
    ),
    cellsToImage: withDefault(
      withIgnoreBlanks(NumberParam), 
      undefined
    ),
    cellConcentration: withDefault(
      NumberParam, 
      DEFAULT_CONCENTRATIONS.imaging
    ),
    imagingConcentration: withDefault(
      NumberParam,
      undefined
    ),
    purityConcentration: withDefault(
      NumberParam,
      undefined
    ),
    yieldConcentration: withDefault(
      NumberParam,
      undefined
    ),
    imagingSampleVolume: withDefault(
      NumberParam,
      undefined
    ),
    puritySampleVolume: withDefault(
      NumberParam,
      undefined
    ),
    yieldSampleVolume: withDefault(
      NumberParam,
      undefined
    ),
    minInputSampleVolume: withDefault(
      NumberParam,
      DEFAULT_SAMPLE_VOLUMES.imaging
    ),
    estimatedRunDuration: withDefault(
      NumberParam,
      DEFAULT_EXPERIMENT_VALUES.ESTIMATED_RUN_DURATION
    ),
    purityWells: withDefault(withIgnoreBlanks(StringParam), undefined),
    yieldWells: withDefault(withIgnoreBlanks(StringParam), undefined)
  })

  // Create experiment data from query parameters, falling back to defaults
  const experimentData: ExperimentData = React.useMemo(() => {
    const data: ExperimentData = { 
      ...defaultExperimentData,
      wells: defaultExperimentData.wells.map(well => ({ ...well }))
    }

    // Apply values from query parameters, with proper defaults
    data.experimentGoal = query.experimentGoal as 'imaging' | 'purity' | 'yield'
    
    // Load mode-specific concentrations
    data.imagingConcentration = (query.imagingConcentration as number) ?? DEFAULT_CONCENTRATIONS.imaging
    data.purityConcentration = (query.purityConcentration as number) ?? DEFAULT_CONCENTRATIONS.purity
    data.yieldConcentration = (query.yieldConcentration as number) ?? DEFAULT_CONCENTRATIONS.yield
    
    // Load mode-specific sample volumes
    data.imagingSampleVolume = (query.imagingSampleVolume as number) ?? DEFAULT_SAMPLE_VOLUMES.imaging
    data.puritySampleVolume = (query.puritySampleVolume as number) ?? DEFAULT_SAMPLE_VOLUMES.purity
    data.yieldSampleVolume = (query.yieldSampleVolume as number) ?? DEFAULT_SAMPLE_VOLUMES.yield
    
    // Use shared concentration across all modes or fall back to query parameter
    data.cellConcentration = (query.cellConcentration as number) ?? DEFAULT_CONCENTRATIONS.imaging
    
    // Set current mode sample volume  
    data.minInputSampleVolume = data[`${data.experimentGoal}SampleVolume` as keyof ExperimentData] as number
    
                             
    data.estimatedRunDuration = query.estimatedRunDuration
    
    if (query.cellsToImage !== undefined && query.cellsToImage !== null) {
      data.cellsToImage = query.cellsToImage
    }
    
    // Set default values only if not provided
    if (query.minInputSampleVolume === undefined || query.minInputSampleVolume === null) {
      data.minInputSampleVolume = DEFAULT_SAMPLE_VOLUMES.imaging
    }
    data.estimatedRunDuration = DEFAULT_EXPERIMENT_VALUES.ESTIMATED_RUN_DURATION

    // Wells are managed separately in RunMetricsEstimator, not through URL
    data.wells = defaultExperimentData.wells.map(well => ({ ...well }))

    return data
  }, [query])

  // Custom setExperimentData that also updates the URL
  const setExperimentData = useCallback((newDataOrUpdater: ExperimentData | ((prev: ExperimentData) => ExperimentData)) => {
    const newData = typeof newDataOrUpdater === 'function' 
      ? newDataOrUpdater(experimentData) 
      : newDataOrUpdater

    // Update URL parameters immediately - always include core parameters
    const newQuery: Record<string, string | number | undefined> = {
      experimentGoal: newData.experimentGoal,
      cellConcentration: newData.cellConcentration, // Store common concentration in URL
      estimatedRunDuration: newData.estimatedRunDuration
    }
    
    // Save all mode-specific concentrations
    newQuery.imagingConcentration = newData.imagingConcentration
    newQuery.purityConcentration = newData.purityConcentration
    newQuery.yieldConcentration = newData.yieldConcentration
    
    // Save all mode-specific sample volumes
    newQuery.imagingSampleVolume = newData.imagingSampleVolume
    newQuery.puritySampleVolume = newData.puritySampleVolume
    newQuery.yieldSampleVolume = newData.yieldSampleVolume
    

    if (newData.experimentGoal === 'imaging') {
      newQuery.cellsToImage = newData.cellsToImage
    }
    
    // Store wells data for URL sharing while keeping modes independent
    if (newData.experimentGoal === 'purity') {
      const wellsData = newData.wells
        .filter(well => {
          const defaultWell = defaultExperimentData.wells.find(d => d.id === well.id)
          if (!defaultWell) return false
          const hasValues = well.outputCells > 0 || well.estimatedFrequency > 0
          const isDifferentFromDefaults = (
            well.outputCells !== defaultWell.outputCells || 
            well.estimatedFrequency !== defaultWell.estimatedFrequency
          )
          return hasValues && isDifferentFromDefaults
        })
        .map(well => `${well.id}:${well.outputCells}:${well.estimatedFrequency}`)
      
      newQuery.purityWells = wellsData.length > 0 ? wellsData.join(',') : undefined
    } else if (newData.experimentGoal === 'yield') {
      const wellsData = newData.wells
        .filter(well => {
          const defaultWell = defaultExperimentData.wells.find(d => d.id === well.id)
          if (!defaultWell) return false
          const hasValues = well.outputCells > 0 || well.estimatedFrequency > 0
          const isDifferentFromDefaults = (
            well.outputCells !== defaultWell.outputCells || 
            well.estimatedFrequency !== defaultWell.estimatedFrequency
          )
          return hasValues && isDifferentFromDefaults
        })
        .map(well => `${well.id}:${well.outputCells}:${well.estimatedFrequency}`)
      
      newQuery.yieldWells = wellsData.length > 0 ? wellsData.join(',') : undefined
    }

    // Clean up unused parameters
    if (newData.experimentGoal !== 'imaging') {
      newQuery.cellsToImage = undefined
    }
    
    delete newQuery.minInputSampleVolume

    setQuery(newQuery)
    
  }, [experimentData, setQuery])

  return {
    experimentData,
    setExperimentData
  }
}

export default useExperimentPlanningParams
